import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isEmpty, omit } from "lodash";

import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { getImageUrl } from "utils";

import {
  useLazyGetCreditNotesDetailsByCreditNoteIdQuery,
  usePostCreditNoteDetailsMutation,
  useUtilityConsumerContactInfoQuery,
} from "apis/UtilityConsumer/utilityConsumer";
import { otherUtilitySubscriptionTypes } from "common/enums";
import moment from "moment";
import DatePicker from "Components/Common/DatePicker";

const CreditNoteForm = ({ t, editModalProps, handleClose }) => {
  const { utilityConsumerId: currentConsumerId, id } = editModalProps ?? {};

  const { data: { data: utilityConsumersOptions } = {} } =
    useUtilityConsumerContactInfoQuery({ isForPowerConsumers: false });

  const [
    getCreditNoteDetails,
    { data: { data: creditNoteData } = {}, isLoading: isCreditNoteLoading },
  ] = useLazyGetCreditNotesDetailsByCreditNoteIdQuery();

  const [
    addCreditNoteDetails,
    {
      data: { data: { url: creditNoteUrl } = {} } = {},
      isLoading: isCreditNoteDataLoading,
    },
  ] = usePostCreditNoteDetailsMutation();

  const [documentToPreview, setDocumentToPreview] = useState(null);

  const initialValues = useMemo(
    () => ({
      id: creditNoteData?.id ?? 0,
      utilityConsumerId:
        creditNoteData?.utilityConsumerId ?? currentConsumerId ?? "",
      consumerDetails: {
        utilityAddress: creditNoteData?.utilityAddress ?? "",
        tariff: creditNoteData?.tariff ?? "",
        consumerName: creditNoteData?.consumerName ?? "",
        consumerNumber: creditNoteData?.consumerNumber ?? "",
        isTemporaryConnection:
          creditNoteData?.utilityConsumer?.isTemporaryConnection ?? false,
      },
      demandCharges: creditNoteData?.demandCharges ?? 0,
      energyCharges: creditNoteData?.energyCharges ?? 0,
      fuelCharges: creditNoteData?.fuelCharges ?? 0,
      otherAmount: creditNoteData?.otherAmount ?? 0,
      meterCharges: creditNoteData?.meterCharges ?? 0,
      subscriptionType: creditNoteData?.subscriptionType ?? "",
      notes: creditNoteData?.notes?.length ? creditNoteData.notes : [""],
      referenceInvoiceNoAndDate: creditNoteData?.referenceInvoiceNoAndDate ?? "",
      
    }),
    [creditNoteData, currentConsumerId]
  );

  const formik = useFormik({
    enableReinitialize: !!currentConsumerId,
    initialValues,
    validationSchema: Yup.object().shape({
      demandCharges: Yup.number()
        .min(0, "Must be positive")
        .required("Demand charge is required"),
      energyCharges: Yup.number()
        .min(0, "Must be positive")
        .required("Energy charge is required"),
      fuelCharges: Yup.number()
        .min(0, "Must be positive")
        .required("Fuel charge is required"),
      otherAmount: Yup.number()
        .min(0, "Must be positive")
        .required("Other amount is required"),
      meterCharges: Yup.number()
        .min(0, "Must be positive")
        .required("Meter charge is required"),
      subscriptionType: Yup.string().required("Subscription type is required"),
      
      // notes: Yup.array().of(Yup.string().required("Note is required")).min(1, "At least one note is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = omit(
          {
            ...values,
            isApproved: false,
            notes: values.notes.filter((note) => !isEmpty(note)),
          },
          "consumerDetails"
        );

        const {
          data: { url },
        } = await addCreditNoteDetails(payload);

        setDocumentToPreview({
          url: getImageUrl(url),
          documentTypeName: "Credit Note Document",
          documentType: "pdf",
          documentName: "Credit Note Document",
        });
      } catch (err) {
        console.error(err);
      }
    },
  });
  const {
    handleSubmit,
    values,
    values: {
      utilityConsumerId,
      subscriptionType,
      notes,
      consumerDetails: { consumerName, tariff, utilityAddress, consumerNumber },
    },
    setFieldValue,
  } = formik;

  const approveBtnHandler = async () => {
    try {
      const payload = omit(
        {
          ...values,
          isApproved: true,
          previewFileUrl: creditNoteUrl ?? "",
          notes: values.notes.filter((note) => !isEmpty(note)),
        },
        "consumerDetails"
      );

      const {
        data: { url },
      } = await addCreditNoteDetails(payload);
      handleClose();
    } catch (error) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          await getCreditNoteDetails(id);
        }
      } catch (error) {}
    })();
  }, [id]);

  if (isCreditNoteLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
            // handleClose();
          }}
          actionButton={
            <Button
              color="success"
              onClick={approveBtnHandler}
              variant="contained"
              disabled={isCreditNoteDataLoading}
            >
              {t("Approve")}
              {isCreditNoteDataLoading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}
      <Modal
        isOpen
        handleClose={handleClose}
        maxWidth="lg"
        title={`${!currentConsumerId ? "Add" : "Edit"} Credit Note Details`}
      >
        <Grid container className="mt-3" columnSpacing={2}>
          {!currentConsumerId && (
            <Grid container item className="d-flex justify-content-between">
              <Grid item xs={12} sm={6}>
                <SelectField
                  label={t("Consumer Number") + "*"}
                  name="utilityConsumerId"
                  formik={formik}
                  options={utilityConsumersOptions || []}
                  onChange={(_, { value }) => {
                    const selectedOption = utilityConsumersOptions.find(
                      ({ id }) => value === id
                    );
                    setFieldValue("utilityConsumerId", value);
                    setFieldValue("consumerDetails", {
                      tariff: selectedOption.tariff,
                      consumerName: selectedOption?.consumerName,
                      utilityAddress: selectedOption.utilityAddress,
                      consumerNumber: selectedOption.consumerNumber,
                      isTemporaryConnection:
                        selectedOption.isTemporaryConnection,
                    });
                    setFieldValue(
                      "subscriptionType",
                      selectedOption.subscriptionType
                    );
                  }}
                  getOptionLabel={({ consumerNumber }) => consumerNumber}
                  getOptionValue={({ id }) => id}
                />
              </Grid>
            </Grid>
          )}

          {!!utilityConsumerId && (
            <Grid
              container
              item
              className="border border-2 mt-2 ps-2 pb-0 ms-2 p-3 mb-2"
            >
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Consumer Name")}: </b>
                  {consumerName}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Consumer Number")}: </b>
                  {consumerNumber}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Tariff")}: </b>
                  {tariff}
                </Text>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Text>
                  <b>{t("Utility Address")}: </b>
                  {`${formatUtilityAddress(
                    utilityAddress
                  )}, Gandhinagar-382050`}
                </Text>
              </Grid>

              <Grid
                container
                columnSpacing={{ xs: 1 }}
                className="border border-2 ps-2 pb-2 p-3 ms-1 mb-2 "
              >
                <Grid item xs={12} sm={6} md={4}>
                  <SelectField
                    label={t("Subscription Type") + "*"}
                    onChange={(_, { value }) => {
                      setFieldValue("subscriptionType", value);
                    }}
                    formik={formik}
                    name="subscriptionType"
                    value={
                      otherUtilitySubscriptionTypes.find(
                        ({ value }) => value === subscriptionType
                      ) ?? ""
                    }
                    options={otherUtilitySubscriptionTypes}
                    disabled
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label={t("Demand Charges") + "*"}
                    name={"demandCharges"}
                    formik={formik}
                    type="number"
                    placeholder={t("Enter Demand Charges") + "*"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label={t("Energy Charges") + "*"}
                    name={"energyCharges"}
                    formik={formik}
                    type="number"
                    placeholder={t("Enter Energy Charges") + "*"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label={t("Fuel Charges") + "*"}
                    name={"fuelCharges"}
                    formik={formik}
                    type="number"
                    placeholder={t("Enter Fuel Charges") + "*"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label={t("Meter Charges") + "*"}
                    name={"meterCharges"}
                    formik={formik}
                    type="number"
                    placeholder={t("Enter Meter Charges") + "*"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputFieldText
                    label={t("Other Amount") + "*"}
                    name={"otherAmount"}
                    formik={formik}
                    type="number"
                    placeholder={t("Enter Other Amount") + "*"}
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label={t("Credit Note Month")}
                    className="w-100 mb-2"
                    format="DD-MM-YYYY"
                    value={
                      creditNoteMonth
                        ? moment(creditNoteMonth, "YYYY-MM-DD")
                        : null
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        error:
                          formik.touched.creditNoteMonth &&
                          Boolean(formik.errors.creditNoteMonth),
                        sx: {
                          "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            marginRight: 0,
                          },
                        },
                        helperText:
                          formik.touched.creditNoteMonth &&
                          formik.errors.creditNoteMonth,
                      },
                    }}
                    onChange={(date) => {
                      setFieldValue(
                        "creditNoteMonth",
                        date ? moment(date).format("YYYY-MM-DD") : ""
                      );
                    }}
                  />
                </Grid> */}
                <Grid item xs={12} sm={12} >
                      <TextField
                        placeholder={t("Reference Invoice No. and Date")}
                        className="mb-3"
                        multiline
                        rows={2}
                        onChange={(e) => {
                          setFieldValue("referenceInvoiceNoAndDate", e.target.value);
                        }}
                        fullWidth
                        value={formik.values.referenceInvoiceNoAndDate}
                        size="small"
                      />
                    </Grid>
                <Grid xs={12} className="ps-2">
                  <Button
                    size="medium"
                    variant="contained"
                    onClick={() => {
                      setFieldValue("notes", [...notes, ""]);
                    }}
                  >
                    {t("Add Notes")}
                  </Button>
                </Grid>
                {notes?.map((item, index) => (
                  <>
                    <Grid item xs={8} sm={8} key={index}>
                      {/* textfield will show error if any */}
                      <TextField
                        placeholder={t("Add a comment") + "..."}
                        className="mb-1 mt-2"
                        multiline
                        rows={3}
                        onChange={(e) => {
                          setFieldValue(`notes[${index}]`, e.target.value);
                        }}
                        fullWidth
                        value={item}
                        error={Boolean(formik.errors.notes?.[index])}
                        helperText={formik.errors.notes?.[index]}
                        size="small"
                      />
                    </Grid>
                    {notes?.length > 1 && (
                      <Grid item>
                        <IconButton
                          onClick={() => {
                            const filteredData = notes.filter(
                              (_, inx) => index !== inx
                            );
                            setFieldValue("notes", filteredData);
                          }}
                        >
                          <CloseIcon color="error" />
                        </IconButton>
                      </Grid>
                    )}
                  </>
                ))}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12} className="d-flex justify-content-end mb-2 mt-2">
            <Button
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={isCreditNoteDataLoading}
              endIcon={
                isCreditNoteDataLoading && <CircularProgress size={20} />
              }
            >
              {t("Preview")}
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

CreditNoteForm.propTypes = {
  t: PropTypes.func,
  editModalProps: PropTypes.object,
  handleClose: PropTypes.func,
};

export default withTranslation()(CreditNoteForm);
